/**
 * Failed-PDF banner — sits at the top of the Dashboard (PDF Inbox) so Joey
 * can see PDFs that arrived but failed to parse. Without this, a parse
 * failure is invisible: the inbox list is built from PDFs that have at least
 * one pending block, and a failed PDF has zero blocks, so it never appears
 * anywhere (audit BLOCKER 2).
 *
 * Data comes from the existing GET /api/intake/health endpoint (30-day
 * window); failed rows get the red section with Retry, parsed_with_warnings
 * rows get the amber section (warnings verbatim, View PDF, no Retry). RED
 * MEANS "CONFIRMED FAILED, NEEDS A HUMAN" (client ruling 2026-08-12) — the API
 * withholds any failed row its retry cron is still working and counts it in
 * `retrying_now` instead, which joins the processing line above. Nothing in
 * this file decides that; it just receives fewer rows. The
 * clean state renders nothing at all — no card, no "all clear" line — so the
 * Dashboard is visually unchanged whenever intake is healthy. Visual
 * vocabulary mirrors PcoNeedsAttention.
 *
 * BOTH SECTIONS FOLD, AND NEITHER DISMISSES (client ask 2026-08-12). Five
 * failed PDFs sat pinned open all morning, pushing the inbox the PM was
 * actually working down the page with no way to put them away. Clicking a
 * headline row folds its section to that one line — the count stays on screen,
 * so nothing is ever hidden, only stood down. Open by default: the fold is a
 * thing the PM chooses, not a thing he has to discover.
 */
import { useState } from 'react';
import { ExpandChevron } from './design';
import { splitIntakeAttention, type IntakeHealthAttentionItem } from '@coffman-tm/shared';
import { useIntakeHealth } from '../lib/useIntakeHealth';
import { api, ApiCallError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { formatDateMd, formatDateTimePacific } from '../lib/format';
import { PdfPreviewModal } from './PdfPreviewModal';

const WINDOW_DAYS = 30;
const COLLAPSE_AT = 10;
/** Intake state moves without the PM touching anything — see useIntakeHealth. */
const POLL_MS = 30_000;

export function FailedPdfsBanner() {
  const { data, isLoading, error, refetch } = useIntakeHealth(WINDOW_DAYS, POLL_MS);
  const [showAll, setShowAll] = useState(false);
  const [previewPdfId, setPreviewPdfId] = useState<string | null>(null);
  // Per-section, component-local, not persisted: a reload re-opens both. The
  // fold is for getting a bad morning out of the way, not a standing setting.
  const [failedOpen, setFailedOpen] = useState(true);
  const [warnedOpen, setWarnedOpen] = useState(true);

  // Render nothing unless there's at least one failed OR warned PDF, or work in
  // flight. Loading, fetch error, and the all-clear state all collapse to null
  // so the banner only ever appears when Joey has something to look at.
  if (isLoading && !data) return null;
  if (error) return null;

  const { failed, warned } = splitIntakeAttention(data?.attention ?? []);
  // Two kinds of in-flight work, one line. `processing_now` is parses running
  // right now; `retrying_now` is failed rows the retry cron is still working —
  // the API keeps those out of `attention` so red stays "needs a human", and a
  // PM should not have to know which lane a report is in. Both `?? 0`: an API
  // older than either field omits it, and a cached bundle can outlive a deploy.
  const inFlight = (data?.processing_now ?? 0) + (data?.retrying_now ?? 0);
  if (failed.length === 0 && warned.length === 0 && inFlight === 0) return null;

  const shown = showAll ? failed : failed.slice(0, COLLAPSE_AT);
  const overflow = failed.length - shown.length;

  return (
    <>
      {/* In-flight work. For the first minutes of a big forward this is the
          ONLY thing on the page that differs from "nothing was sent" — no PDF
          has failed, none has warned, none is old enough to look stuck, and
          the inbox list is still empty because no blocks exist yet. A PM who
          can't tell those apart re-forwards, which de-dupes silently.
          It also carries the retry lane: a report whose parse was cut off
          spends up to an hour being retried, and that is "still processing",
          not a failure to act on. The copy deliberately does NOT mention the
          retry mechanics (client ruling 2026-08-12: a simple communication,
          nothing alarming) — the help topic carries that detail. */}
      {inFlight > 0 && (
        <div style={processingCardStyle}>
          <span className="muted" style={{ fontSize: 13 }}>
            {inFlight} PDF{inFlight === 1 ? '' : 's'} still processing — they’ll appear in the
            inbox as they finish.
          </span>
        </div>
      )}
      {failed.length > 0 && (
        <div style={cardStyle}>
          <SectionHead
            expanded={failedOpen}
            onToggle={() => setFailedOpen((v) => !v)}
            glyphColor="var(--danger)"
          >
            {failed.length} failed PDF{failed.length === 1 ? '' : 's'} in the last {WINDOW_DAYS} days
          </SectionHead>
          {failedOpen && (
            <>
              <ul style={listStyle}>
                {shown.map((item) => (
                  <FailedRow key={item.daily_pdf_id} item={item} onRecovered={refetch} />
                ))}
              </ul>
              {failed.length > COLLAPSE_AT && (
                <button
                  type="button"
                  className="btn-link"
                  style={{ marginTop: 10 }}
                  onClick={() => setShowAll((v) => !v)}
                >
                  {showAll ? 'Show fewer' : `Show all ${failed.length}`}
                  <ExpandChevron expanded={showAll} />
                </button>
              )}
              {overflow > 0 && !showAll && (
                <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>
                  ({overflow} more)
                </span>
              )}
            </>
          )}
        </div>
      )}
      {warned.length > 0 && (
        <div style={warnCardStyle}>
          <SectionHead
            expanded={warnedOpen}
            onToggle={() => setWarnedOpen((v) => !v)}
            glyphColor="var(--warning)"
          >
            {warned.length} PDF{warned.length === 1 ? '' : 's'} parsed with warnings in the last{' '}
            {WINDOW_DAYS} days
          </SectionHead>
          {warnedOpen && (
            <ul style={listStyle}>
              {warned.map((item) => (
                <WarnedRow
                  key={item.daily_pdf_id}
                  item={item}
                  onViewPdf={() => setPreviewPdfId(item.daily_pdf_id)}
                />
              ))}
            </ul>
          )}
        </div>
      )}
      <PdfPreviewModal source={previewPdfId} onClose={() => setPreviewPdfId(null)} />
    </>
  );
}

/**
 * The headline row of either section, and its disclosure control — the whole
 * row is the target, not a trailing link, so folding a section is one click
 * anywhere along the line that named the problem.
 *
 * Same idiom as the collapsible section heads on the PCO / Rollup / Settings
 * pages (`role="button"` + Enter/Space + a leading `ExpandChevron`); those
 * carry it on `.section-head.clickable`, this file styles inline, so the
 * three things that class supplies are stated here: the cursor, the
 * select-suppression, and the hover accent on the headline
 * (`.section-head.clickable:hover .section-title`, index.css). The hover
 * colors the TITLE only — the severity glyph keeps its own red/amber, which is
 * information, not chrome.
 */
function SectionHead({
  expanded,
  onToggle,
  glyphColor,
  children,
}: {
  expanded: boolean;
  onToggle: () => void;
  /** Severity of the section the row heads: danger for failed, warning for parsed-with-warnings. */
  glyphColor: string;
  children: React.ReactNode;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={expanded ? 'Click to collapse' : 'Click to expand'}
      style={expanded ? titleStyle : collapsedTitleStyle}
    >
      <span style={{ color: 'var(--ink-3)', display: 'inline-flex' }}>
        <ExpandChevron expanded={expanded} size={14} />
      </span>
      <span style={{ color: glyphColor, lineHeight: 1 }} aria-hidden>
        ⚠
      </span>
      <span style={hovered ? headlineHoverStyle : headlineStyle}>{children}</span>
    </div>
  );
}

function WarnedRow({
  item,
  onViewPdf,
}: {
  item: IntakeHealthAttentionItem;
  onViewPdf: () => void;
}) {
  const label = item.email_subject?.trim() || fallbackLabel(item);
  return (
    <li>
      <div style={{ color: 'var(--ink)', fontSize: 13, lineHeight: 1.35 }}>{label}</div>
      <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
        {item.email_from} · {formatDateTimePacific(item.received_at)}
      </div>
      {/* Warning strings are written for humans (cross-field validator) —
          render them verbatim, no rewording. */}
      <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
        {item.parse_warnings.map((w, i) => (
          <li key={i} style={{ fontSize: 12, lineHeight: 1.4, color: 'var(--warning)' }}>
            {w}
          </li>
        ))}
      </ul>
      {/* NO Retry here: the PDF parsed (reparse is failed-only — it would 409).
          The row clears on its own once every block on the PDF is decided. */}
      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-ghost" onClick={onViewPdf}>
          View PDF
        </button>
        <span style={{ fontSize: 12, lineHeight: 1.35, color: 'var(--ink-2)' }}>
          The data landed in the Inbox — check the flagged rows against the original before saving.
        </span>
      </div>
    </li>
  );
}

function FailedRow({
  item,
  onRecovered,
}: {
  item: IntakeHealthAttentionItem;
  onRecovered: () => void;
}) {
  const { token } = useAuth();
  const [state, setState] = useState<'idle' | 'retrying' | 'error'>('idle');
  const [retryMsg, setRetryMsg] = useState('');

  // daily_pdfs has no attachment-filename column; the foreman's email subject
  // is the closest human-recognizable identity, falling back to foreman/date.
  const label = item.email_subject?.trim() || fallbackLabel(item);
  // Raw parser errors (e.g. an Anthropic 500) are intentionally NOT shown to
  // the PM — they stay in the DB / intake-health for our diagnosis. The user
  // sees a clean message + a Retry button instead.

  async function onRetry() {
    if (!token || state === 'retrying') return;
    setState('retrying');
    setRetryMsg('');
    try {
      const res = await api.intake.reparse(token, item.daily_pdf_id);
      // Recovered → refetch health so this row drops out of the banner and the
      // PDF surfaces in the inbox on its next list refresh.
      if (res.status === 'parsed' || res.status === 'parsed_with_warnings') {
        onRecovered();
        return;
      }
      // Errored again — typically a still-ongoing upstream blip.
      setState('error');
      setRetryMsg(
        'Still failing — the parser service may be having a brief blip. Try again in a moment.',
      );
    } catch (err) {
      // 409 = another tab or the cron already handled it; just refresh it away.
      if (err instanceof ApiCallError && err.status === 409) {
        onRecovered();
        return;
      }
      setState('error');
      setRetryMsg('Retry failed. Try again in a moment.');
    }
  }

  return (
    <li>
      <div style={{ color: 'var(--ink)', fontSize: 13, lineHeight: 1.35 }}>{label}</div>
      <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
        {item.email_from} · {formatDateTimePacific(item.received_at)}
      </div>
      {/* A PDF that fails never routes, so it belongs to no job yet and is
          shown to every PM — say so, or it reads as "a failure on YOUR job". */}
      {item.is_unrouted && (
        <div className="muted" style={{ fontSize: 12, marginTop: 2, fontStyle: 'italic' }}>
          Didn’t get far enough to be matched to a job — it may not be yours.
        </div>
      )}
      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn btn-danger"
          onClick={onRetry}
          disabled={state === 'retrying'}
        >
          {state === 'retrying' ? 'Retrying…' : 'Retry'}
        </button>
        <span
          style={{
            fontSize: 12,
            lineHeight: 1.35,
            color: state === 'error' ? 'var(--danger)' : 'var(--ink-2)',
          }}
        >
          {state === 'error' && retryMsg
            ? retryMsg
            : "This report didn't process automatically. Give it another try."}
        </span>
      </div>
    </li>
  );
}

function fallbackLabel(item: IntakeHealthAttentionItem): string {
  const parts: string[] = [];
  if (item.foreman_name) parts.push(item.foreman_name);
  if (item.report_date) parts.push(formatDateMd(item.report_date));
  return parts.length > 0 ? parts.join(' · ') : '(no subject)';
}

const processingCardStyle: React.CSSProperties = {
  padding: '10px var(--pad-x)',
  background: 'var(--surface)',
  borderBottom: '1px solid var(--rule)',
};

const cardStyle: React.CSSProperties = {
  padding: '14px var(--pad-x) 16px',
  background: 'var(--surface-warm)',
  borderBottom: '1px solid var(--rule-strong)',
};

const warnCardStyle: React.CSSProperties = {
  padding: '14px var(--pad-x) 16px',
  background: 'var(--surface-warm)',
  borderBottom: '1px solid var(--rule-strong)',
  borderLeft: '3px solid var(--warning)',
};

const titleStyle: React.CSSProperties = {
  fontFamily: 'var(--sans)',
  fontSize: 15,
  fontWeight: 500,
  color: 'var(--ink)',
  lineHeight: 1.3,
  marginBottom: 10,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  cursor: 'pointer',
  userSelect: 'none',
};

// Folded, the head IS the section — the bottom margin exists to separate it
// from a list that is no longer there.
const collapsedTitleStyle: React.CSSProperties = { ...titleStyle, marginBottom: 0 };

// The headline text inside the head row. Same transition the CSS idiom puts on
// `.section-title`, so the hover accent eases in rather than snapping.
const headlineStyle: React.CSSProperties = { transition: 'color .12s' };

const headlineHoverStyle: React.CSSProperties = { ...headlineStyle, color: 'var(--accent)' };

const listStyle: React.CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};
